import type { DeviceInfo } from '@/types';

const DEVICE_STORAGE_KEY = 'five_ai_device_id';

// NOTE:
// "Uniquely identify the device" means different things across platforms:
// - Web: browsers intentionally do not expose a stable hardware identifier. The best-practice approach
//   is a *stable installation identifier* persisted in storage (unique per browser profile / WebView).
// - Mobile: native apps can provide a stable device identifier; we support an optional injected override.
//
// For the web portal, we generate a RFC4122 v4 UUID and persist it so onboarding can resume reliably.
const UUID_V4_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const generateUuidV4 = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // RFC4122 v4 using cryptographically secure random bytes
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    // Per RFC4122 section 4.4
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Very old / restricted environments: uniqueness best-effort (still persisted once generated).
  return `device-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const cookie of cookies) {
    const [k, ...rest] = cookie.split('=');
    if (k === name) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return null;
};

const writeCookie = (name: string, value: string, maxAgeSeconds = 60 * 60 * 24 * 365 * 2) => {
  if (typeof document === 'undefined') return;
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
};

const getInjectedDeviceId = (): string | null => {
  if (typeof window === 'undefined') return null;
  const candidate =
    (window as unknown as { FIVE_DEVICE_ID?: unknown }).FIVE_DEVICE_ID ??
    (window as unknown as { __FIVE_DEVICE_ID?: unknown }).__FIVE_DEVICE_ID;
  if (typeof candidate !== 'string') return null;
  const trimmed = candidate.trim();
  if (!trimmed) return null;
  // We only accept UUID v4 to keep API semantics consistent (and prevent surprise formats).
  return UUID_V4_REGEX.test(trimmed) ? trimmed : null;
};

const getStoredDeviceId = () => {
  if (typeof window === 'undefined') {
    return generateUuidV4();
  }

  // Allow native/mobile shells (WebView) to inject a stable device id.
  // This should be set before the app loads (e.g. via JS injection).
  const injected = getInjectedDeviceId();
  if (injected) {
    try {
      localStorage.setItem(DEVICE_STORAGE_KEY, injected);
    } catch {
      // ignore
    }
    writeCookie(DEVICE_STORAGE_KEY, injected);
    return injected;
  }

  const existing =
    localStorage.getItem(DEVICE_STORAGE_KEY) ||
    readCookie(DEVICE_STORAGE_KEY);
  if (existing) {
    // Best-effort rehydrate both stores so we stay stable even if one is cleared.
    try {
      localStorage.setItem(DEVICE_STORAGE_KEY, existing);
    } catch {
      // ignore
    }
    writeCookie(DEVICE_STORAGE_KEY, existing);
    return existing;
  }

  const newId = generateUuidV4();
  try {
    localStorage.setItem(DEVICE_STORAGE_KEY, newId);
  } catch {
    // ignore
  }
  writeCookie(DEVICE_STORAGE_KEY, newId);
  return newId;
};

export const getBrowserDeviceInfo = (): DeviceInfo => {
  if (typeof window === 'undefined') {
    return {
      deviceId: generateUuidV4(),
      deviceType: 'SERVER',
    };
  }

  const navigatorInfo = window.navigator;
  const screenInfo = window.screen;
  const language = navigatorInfo.language || navigatorInfo.languages?.[0];

  const uaData =
    'userAgentData' in navigatorInfo
      ? (navigatorInfo as Navigator & { userAgentData?: { platform?: string } })
          .userAgentData
      : undefined;

  // Extract OS version from userAgent (API requires max 50 chars)
  // Try to get a short OS identifier from platform or userAgent
  let osVersion: string | undefined;
  const platform = uaData?.platform || navigatorInfo.platform || '';
  
  if (platform && platform.length <= 50) {
    osVersion = platform; // e.g., "MacIntel", "Win32", "Linux x86_64"
  } else {
    // Extract OS name from userAgent if platform is too long or unavailable
    const userAgent = navigatorInfo.userAgent || '';
    const osMatch = userAgent.match(/(Mac OS X|Windows|Linux|Android|iOS)[\s/]?([\d._]+)?/i);
    if (osMatch) {
      // Use OS name and version if available, but truncate to 50 chars
      const osString = osMatch.slice(1).filter(Boolean).join(' ');
      osVersion = osString.length > 50 ? osString.substring(0, 47) + '...' : osString;
    } else if (userAgent) {
      // Last resort: just take first 50 chars of userAgent
      osVersion = userAgent.substring(0, 50);
    }
  }

  // Truncate userAgent to 100 chars (API requirement)
  const fullUserAgent = navigatorInfo.userAgent || '';
  const truncatedUserAgent = fullUserAgent.length > 100 
    ? fullUserAgent.substring(0, 100) 
    : fullUserAgent;

  return {
    deviceId: getStoredDeviceId(),
    deviceType: 'WEB',
    deviceModel: uaData?.platform || navigatorInfo.platform,
    deviceBrand: navigatorInfo.vendor || 'Unknown',
    ...(osVersion && { osVersion }), // Only include if we have a valid short version
    appVersion: import.meta.env.FIVE_APP_VERSION || '1.0.0',
    screenResolution: `${screenInfo.width}x${screenInfo.height}`,
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    language,
    userAgent: truncatedUserAgent,
    // NOTE:
    // The onboarding API requires an IP address in the payload and rejects
    // non-routable values like 0.0.0.0 / 127.0.0.1. We send a public placeholder
    // and rely on the backend/proxy to capture the real client IP from headers.
    ipAddress: '1.1.1.1',
  };
};

